import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';

import { useAuth } from '@/hooks/useAuth';
import { canViewUsers, getActiveGrants, isManager, isSuperAdmin } from '@/lib/grant-utils';
import { router } from '@/lib/router';
import { useAppStore } from '@/store/store';

export function AppRouter(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();
  const { userdetail } = useAppStore();

  const activeGrants = getActiveGrants(userdetail?.grants, userdetail?.lastActiveOrgId);
  const activeRoleGrants = activeGrants.filter(g => g.roleName === userdetail?.role);
  const managerState = isManager(activeRoleGrants);
  const viewUsersState = canViewUsers(activeRoleGrants);
  const manageOrgsState = isSuperAdmin(userdetail?.grants);

  // Invalidate router when auth state or permissions change so route guards re-evaluate
  useEffect(() => {
    void router.invalidate();
  }, [isAuthenticated, isLoading, managerState, viewUsersState, manageOrgsState]);

  return (
    <RouterProvider
      context={{
        queryClient,
        auth: {
          isAuthenticated,
          isLoading,
          isManager: managerState,
          canViewUsers: viewUsersState,
          canManageOrgs: manageOrgsState,
        },
      }}
      router={router}
    />
  );
}
